/**
 * CSV Processing Utilities Module
 * Handles CSV parsing, validation, and data extraction
 * Extracted from csvImportApiClient.js for better maintainability
 */

const DEBUG_API = process.env.REACT_APP_DEBUG_API === 'true';

class CSVProcessingUtils {

    /**
     * Parse CSV header and validate structure
     * @param {Object} csvData - Parsed CSV data
     * @param {Object} profile - Profile configuration
     * @returns {Object} Validation result with headers and errors
     */
    parseCSVHeader(csvData, profile) {
        const result = {
            success: true,
            headers: [],
            errors: [],
            warnings: []
        };

        if (!csvData || !csvData.data || csvData.data.length === 0) {
            result.success = false;
            result.errors.push('CSV-Datei ist leer oder ungültig');
            return result;
        }

        // Get headers from first row
        result.headers = Object.keys(csvData.data[0]);

        if (result.headers.length === 0) {
            result.success = false;
            result.errors.push('Keine CSV-Header gefunden');
            return result;
        }

        // Validate against profile field mapping if available
        if (profile && profile.fieldMapping) {
            const mappedFields = Object.keys(profile.fieldMapping);
            const missingFields = [];

            for (const field of mappedFields) {
                if (!result.headers.includes(field)) {
                    missingFields.push(field);
                }
            }

            if (missingFields.length > 0) {
                result.warnings.push(`Fehlende CSV-Spalten für Profil-Mapping: ${missingFields.join(', ')}`);
            }
        }

        if (DEBUG_API) {
            console.log('[CSV Processing] Headers found:', result.headers);
        }

        return result;
    }

    /**
     * Validate CSV data structure and content
     * @param {Object} csvData - Parsed CSV data
     * @param {Object} profile - Profile configuration
     * @returns {Object} Validation result
     */
    validateCSVData(csvData, profile) {
        const result = {
            success: true,
            errors: [],
            warnings: [],
            stats: {
                totalRows: 0,
                validRows: 0,
                invalidRows: 0,
                emptyRows: 0
            }
        };

        if (!csvData || !csvData.data) {
            result.success = false;
            result.errors.push('Ungültige CSV-Daten');
            return result;
        }

        const rows = csvData.data;
        result.stats.totalRows = rows.length;

        // Validate each row
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            const rowNumber = i + 1;

            // Check for empty rows
            const values = Object.values(row);
            const hasData = values.some(value => value && value.toString().trim() !== '');

            if (!hasData) {
                result.stats.emptyRows++;
                continue;
            }

            // Validate row against profile requirements
            const rowValidation = this.validateRow(row, profile, rowNumber);
            
            if (rowValidation.isValid) {
                result.stats.validRows++;
            } else {
                result.stats.invalidRows++;
                result.errors.push(...rowValidation.errors);
                result.warnings.push(...rowValidation.warnings);
            }
        }

        // Overall validation
        if (result.stats.validRows === 0) {
            result.success = false;
            result.errors.push('Keine gültigen Datenzeilen gefunden');
        }

        if (result.stats.invalidRows > result.stats.validRows) {
            result.warnings.push(`Mehr ungültige (${result.stats.invalidRows}) als gültige (${result.stats.validRows}) Zeilen gefunden`);
        }

        if (DEBUG_API) {
            console.log('[CSV Processing] Validation stats:', result.stats);
        }

        return result;
    }

    /**
     * Validate individual CSV row
     * @param {Object} row - CSV row data
     * @param {Object} profile - Profile configuration
     * @param {number} rowNumber - Row number for error reporting
     * @returns {Object} Row validation result
     */
    validateRow(row, profile, rowNumber) {
        const result = {
            isValid: true,
            errors: [],
            warnings: []
        };

        // Basic validation - check for required fields based on profile
        if (profile && profile.fieldMapping) {
            // Check for product number (required)
            const productFields = Object.keys(profile.fieldMapping).filter(key => 
                profile.fieldMapping[key].includes('productNumber') || 
                key.toLowerCase().includes('product') ||
                key.toLowerCase().includes('artikel')
            );

            if (productFields.length > 0) {
                const productField = productFields[0];
                if (!row[productField] || row[productField].toString().trim() === '') {
                    result.isValid = false;
                    result.errors.push(`Zeile ${rowNumber}: Produktnummer fehlt (${productField})`);
                }
            }

            // Check for quantity (required)
            const quantityFields = Object.keys(profile.fieldMapping).filter(key => 
                profile.fieldMapping[key].includes('quantity') || 
                key.toLowerCase().includes('quantity') ||
                key.toLowerCase().includes('menge') ||
                key.toLowerCase().includes('anzahl')
            );

            if (quantityFields.length > 0) {
                const quantityField = quantityFields[0];
                const quantity = row[quantityField];
                if (!quantity || isNaN(parseInt(quantity)) || parseInt(quantity) <= 0) {
                    result.isValid = false;
                    result.errors.push(`Zeile ${rowNumber}: Ungültige Menge (${quantityField})`);
                }
            }
        }

        return result;
    }

    /**
     * Perform plausibility checks on CSV data
     * @param {Object} csvData - CSV data
     * @param {Object} profile - Profile configuration
     * @param {Object} customer - Selected customer
     * @param {string} salesChannelId - Sales channel ID
     * @returns {Object} Plausibility check result
     */
    performPlausibilityCheck(csvData, profile, customer, salesChannelId) {
        const results = {
            success: true,
            errors: [],
            warnings: [],
            stats: {
                totalProducts: 0,
                uniqueProducts: 0,
                totalQuantity: 0,
                estimatedValue: 0
            }
        };

        // Basic validation
        if (!csvData || !csvData.data || csvData.data.length === 0) {
            results.errors.push('Keine CSV-Daten zum Prüfen vorhanden');
            results.success = false;
            return results;
        }

        if (!profile) {
            results.errors.push('Kein Profil für Plausibilitätsprüfung vorhanden');
            results.success = false;
            return results;
        }

        if (!customer) {
            results.errors.push('Kein Kunde ausgewählt');
            results.success = false;
            return results;
        }

        if (!salesChannelId) {
            results.errors.push('Kein Verkaufskanal konfiguriert');
        }

        if (!profile.defaultValues?.paymentMethodId) {
            results.warnings.push('Keine Zahlungsart konfiguriert - Standard wird verwendet');
        }

        if (!profile.defaultValues?.shippingMethodId) {
            results.warnings.push('Keine Versandart konfiguriert - Standard wird verwendet');
        }

        // Analyze CSV data
        const productNumbers = new Set();
        let totalQuantity = 0;

        for (const row of csvData.data) {
            // Extract product number
            const productData = this.extractProductDataFromRow(row, profile);
            if (productData.productNumber) {
                productNumbers.add(productData.productNumber);
                totalQuantity += productData.quantity;
            }
        }

        results.stats.totalProducts = csvData.data.length;
        results.stats.uniqueProducts = productNumbers.size;
        results.stats.totalQuantity = totalQuantity;

        // Plausibility warnings
        if (results.stats.uniqueProducts < results.stats.totalProducts * 0.5) {
            results.warnings.push(`Viele doppelte Produkte gefunden (${results.stats.uniqueProducts} einzigartig von ${results.stats.totalProducts})`);
        }

        if (totalQuantity > 10000) {
            results.warnings.push(`Sehr hohe Gesamtmenge: ${totalQuantity} Stück`);
        }

        if (results.stats.totalProducts > 1000) {
            results.warnings.push(`Sehr viele Positionen: ${results.stats.totalProducts} Zeilen`);
        }

        if (DEBUG_API) {
            console.log('[CSV Processing] Plausibility check results:', results);
        }

        return results;
    }

    /**
     * Extract product data from CSV row using profile mapping
     * @param {Object} row - CSV row data
     * @param {Object} profile - Profile configuration
     * @returns {Object} Extracted product data
     */
    extractProductDataFromRow(row, profile) {
        const fieldMapping = profile.fieldMapping || {};
        
        // Find product number mapping
        const productMappingKey = Object.keys(fieldMapping).find(key => 
            fieldMapping[key].includes('productNumber') || 
            key.toLowerCase().includes('product') ||
            key.toLowerCase().includes('artikel')
        );
        
        // Find quantity mapping
        const quantityMappingKey = Object.keys(fieldMapping).find(key => 
            fieldMapping[key].includes('quantity') || 
            key.toLowerCase().includes('quantity') ||
            key.toLowerCase().includes('menge') ||
            key.toLowerCase().includes('anzahl')
        );
        
        // Find product name mapping
        const nameMappingKey = Object.keys(fieldMapping).find(key => 
            fieldMapping[key].includes('name') || 
            key.toLowerCase().includes('name') ||
            key.toLowerCase().includes('bezeichnung')
        );

        // Find price mapping
        const priceMappingKey = Object.keys(fieldMapping).find(key => 
            fieldMapping[key].includes('price') || 
            key.toLowerCase().includes('price') ||
            key.toLowerCase().includes('preis')
        );
        
        return {
            productNumber: productMappingKey ? row[productMappingKey] : null,
            quantity: quantityMappingKey ? parseInt(row[quantityMappingKey]) || 1 : 1,
            productName: nameMappingKey ? row[nameMappingKey] : null,
            unitPrice: priceMappingKey ? this.parsePrice(row[priceMappingKey]) : 0
        };
    }

    /**
     * Parse price from various formats
     * @param {string|number} priceValue - Price value to parse
     * @returns {number} Parsed price or 0
     */
    parsePrice(priceValue) {
        if (typeof priceValue === 'number') {
            return priceValue;
        }

        if (typeof priceValue === 'string') {
            // Handle German decimal format (123,45) and English format (123.45)
            const normalizedPrice = priceValue
                .replace(/[^\d,.-]/g, '') // Remove non-numeric characters except comma, dot, dash
                .replace(/,/g, '.'); // Replace comma with dot for consistent parsing
            
            const parsed = parseFloat(normalizedPrice);
            return isNaN(parsed) ? 0 : parsed;
        }

        return 0;
    }

    /**
     * Validate order dates in CSV data
     * @param {Object} csvData - CSV data
     * @param {Object} profile - Profile configuration
     * @returns {Object} Date validation result
     */
    validateOrderDates(csvData, profile) {
        const result = {
            success: true,
            errors: [],
            warnings: [],
            dateRange: {
                earliest: null,
                latest: null
            }
        };

        if (!csvData || !csvData.data || csvData.data.length === 0) {
            return result;
        }

        // Find date fields in profile mapping
        const dateFields = [];
        if (profile.fieldMapping) {
            Object.keys(profile.fieldMapping).forEach(key => {
                const mapping = profile.fieldMapping[key];
                if (mapping.includes('orderDateTime') || 
                    mapping.includes('orderDate') || 
                    key.toLowerCase().includes('date') ||
                    key.toLowerCase().includes('datum')) {
                    dateFields.push(key);
                }
            });
        }

        if (dateFields.length === 0) {
            result.warnings.push('Keine Datumsfelder in Profil-Mapping gefunden');
            return result;
        }

        const dates = [];
        let invalidDates = 0;

        // Validate dates in each row
        for (let i = 0; i < csvData.data.length; i++) {
            const row = csvData.data[i];
            const rowNumber = i + 1;

            for (const dateField of dateFields) {
                if (row[dateField]) {
                    const parsedDate = this.parseOrderDate(row[dateField]);
                    if (parsedDate) {
                        dates.push(parsedDate);
                    } else {
                        invalidDates++;
                        result.warnings.push(`Zeile ${rowNumber}: Ungültiges Datum in ${dateField}: ${row[dateField]}`);
                    }
                }
            }
        }

        // Analyze date range
        if (dates.length > 0) {
            result.dateRange.earliest = new Date(Math.min(...dates));
            result.dateRange.latest = new Date(Math.max(...dates));

            // Check for future dates
            const now = new Date();
            const futureDates = dates.filter(date => date > now);
            if (futureDates.length > 0) {
                result.warnings.push(`${futureDates.length} Bestellungen mit zukünftigen Daten gefunden`);
            }

            // Check for very old dates
            const oneYearAgo = new Date();
            oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
            const oldDates = dates.filter(date => date < oneYearAgo);
            if (oldDates.length > 0) {
                result.warnings.push(`${oldDates.length} Bestellungen älter als ein Jahr gefunden`);
            }
        }

        if (invalidDates > 0) {
            result.warnings.push(`${invalidDates} ungültige Datumswerte gefunden`);
        }

        return result;
    }

    /**
     * Parse order date from various formats
     * @param {string} dateValue - Date value from CSV
     * @returns {Date|null} Parsed date or null if invalid
     */
    parseOrderDate(dateValue) {
        if (!dateValue) return null;
        
        try {
            // Common date formats
            const dateFormats = [
                // ISO formats
                /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // 2024-01-15T10:30:00
                /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/, // 2024-01-15 10:30:00
                /^\d{4}-\d{2}-\d{2}/, // 2024-01-15
                // German formats
                /^\d{2}\.\d{2}\.\d{4} \d{2}:\d{2}:\d{2}/, // 15.01.2024 10:30:00
                /^\d{2}\.\d{2}\.\d{4}/, // 15.01.2024
                // US formats
                /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}/, // 01/15/2024 10:30:00
                /^\d{2}\/\d{2}\/\d{4}/ // 01/15/2024
            ];
            
            // Try direct parsing first
            let parsedDate = new Date(dateValue);
            if (!isNaN(parsedDate.getTime())) {
                return parsedDate;
            }
            
            // Try format-specific parsing
            for (const format of dateFormats) {
                if (format.test(dateValue)) {
                    if (dateValue.includes('.')) {
                        // German format - convert to ISO
                        const parts = dateValue.split(/[.\s:]/);
                        if (parts.length >= 3) {
                            const day = parts[0];
                            const month = parts[1];
                            const year = parts[2];
                            const hour = parts[3] || '00';
                            const minute = parts[4] || '00';
                            const second = parts[5] || '00';
                            
                            parsedDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
                            if (!isNaN(parsedDate.getTime())) {
                                return parsedDate;
                            }
                        }
                    }
                    break;
                }
            }
            
            return null;
        } catch (error) {
            console.warn('[CSV Processing] Error parsing date:', dateValue, error);
            return null;
        }
    }

    /**
     * Process CSV data and extract product information
     * @param {Object} csvData - Parsed CSV data
     * @param {Object} profile - Profile configuration
     * @returns {Array} Array of processed CSV items
     */
    parseCSVData(csvData, profile) {
        if (!csvData || !csvData.data) {
            return [];
        }

        const csvProductData = [];
        
        for (let index = 0; index < csvData.data.length; index++) {
            const row = csvData.data[index];
            const productData = this.extractProductDataFromRow(row, profile);
            
            if (productData.productNumber) {
                csvProductData.push({
                    index,
                    row,
                    productNumber: productData.productNumber,
                    productName: productData.productName,
                    quantity: productData.quantity,
                    unitPrice: productData.unitPrice
                });
            }
        }

        if (DEBUG_API) {
            console.log('[CSV Processing] Processed CSV data:', csvProductData.length, 'items');
        }

        return csvProductData;
    }

    /**
     * Group CSV rows by order number (extracted from CSV data)
     * @param {Object} csvData - Parsed CSV data with headers and rows
     * @param {Object} profile - Profile configuration with field mapping
     * @returns {Object} Grouped orders by order number
     */
    groupCSVRowsByOrderNumber(csvData, profile) {
        const groupedOrders = {};
        
        if (!csvData || !csvData.data || csvData.data.length === 0) {
            return groupedOrders;
        }

        for (let rowIndex = 0; rowIndex < csvData.data.length; rowIndex++) {
            const row = csvData.data[rowIndex];
            
            // Extract order number from CSV row using field mapping
            const orderNumber = this.extractOrderNumberFromRow(row, profile, rowIndex);
            
            // Extract product number from CSV row
            const productNumber = this.extractProductNumberFromRow(row, profile);
            
            if (!productNumber) {
                console.warn(`[CSV Processing] Skipping row ${rowIndex + 1} - no product number found`);
                continue;
            }

            // Initialize order group if not exists
            if (!groupedOrders[orderNumber]) {
                groupedOrders[orderNumber] = {};
            }

            // Initialize product group if not exists
            if (!groupedOrders[orderNumber][productNumber]) {
                groupedOrders[orderNumber][productNumber] = [];
            }

            // Add row data to product group
            groupedOrders[orderNumber][productNumber].push({
                rowIndex: rowIndex,
                row: row,
                quantity: this.extractQuantityFromRow(row, profile),
                price: this.extractPriceFromRow(row, profile),
                productNumber: productNumber
            });
        }

        if (DEBUG_API) {
            console.log('[CSV Processing] Grouped orders:', Object.keys(groupedOrders).length);
            for (const [orderNumber, products] of Object.entries(groupedOrders)) {
                console.log(`  Order ${orderNumber}: ${Object.keys(products).length} unique products`);
            }
        }
        
        return groupedOrders;
    }

    /**
     * Consolidate products with same product number in same order
     * @param {Object} productsInOrder - Products grouped by product number for one order
     * @param {string} orderNumber - Order number for logging
     * @returns {Object} Consolidated CSV data with merged products
     */
    consolidateOrderProducts(productsInOrder, orderNumber) {
        const consolidatedRows = [];
        
        for (const [productNumber, productRows] of Object.entries(productsInOrder)) {
            // Consolidate quantities for same product
            let consolidatedQuantity = 0;
            let templateRow = null;
            
            for (const rowData of productRows) {
                consolidatedQuantity += rowData.quantity || 1;
                
                // Use first row as template for consolidated row
                if (!templateRow) {
                    templateRow = { ...rowData.row };
                }
            }
            
            // Update template row with consolidated quantity
            if (templateRow) {
                // Update quantity in template row based on field mapping
                const quantityField = this.findQuantityFieldInRow(templateRow);
                if (quantityField) {
                    templateRow[quantityField] = consolidatedQuantity;
                }
                
                consolidatedRows.push({
                    row: templateRow,
                    quantity: consolidatedQuantity,
                    productNumber: productNumber,
                    originalRowCount: productRows.length
                });
            }
        }

        if (DEBUG_API) {
            console.log(`[CSV Processing] Order ${orderNumber} consolidated: ${Object.keys(productsInOrder).length} products, ${consolidatedRows.length} final rows`);
        }
        
        return {
            data: consolidatedRows.map(item => item.row),
            consolidatedItems: consolidatedRows
        };
    }

    /**
     * Extract order number from CSV row
     * @param {Object} row - CSV row data
     * @param {Object} profile - Profile configuration
     * @param {number} rowIndex - Row index for fallback
     * @returns {string} Order number
     */
    extractOrderNumberFromRow(row, profile, rowIndex = 0) {
        const fieldMapping = profile.fieldMapping || {};
        
        // Find order number mapping
        const orderNumberField = Object.keys(fieldMapping).find(key => 
            fieldMapping[key].includes('orderNumber') || 
            key.toLowerCase().includes('order') ||
            key.toLowerCase().includes('bestellung') ||
            key.toLowerCase().includes('auftrag')
        );
        
        if (orderNumberField && row[orderNumberField]) {
            return row[orderNumberField].toString().trim();
        }
        
        // Fallback: use row index as order number
        return `ORDER_${rowIndex + 1}`;
    }

    /**
     * Extract product number from CSV row
     * @param {Object} row - CSV row data
     * @param {Object} profile - Profile configuration
     * @returns {string|null} Product number or null
     */
    extractProductNumberFromRow(row, profile) {
        const fieldMapping = profile.fieldMapping || {};
        
        // Find product number mapping
        const productNumberField = Object.keys(fieldMapping).find(key => 
            fieldMapping[key].includes('productNumber') || 
            key.toLowerCase().includes('product') ||
            key.toLowerCase().includes('artikel')
        );
        
        if (productNumberField && row[productNumberField]) {
            return row[productNumberField].toString().trim();
        }
        
        return null;
    }

    /**
     * Extract quantity from CSV row
     * @param {Object} row - CSV row data
     * @param {Object} profile - Profile configuration
     * @returns {number} Quantity
     */
    extractQuantityFromRow(row, profile) {
        const fieldMapping = profile.fieldMapping || {};
        
        // Find quantity mapping
        const quantityField = Object.keys(fieldMapping).find(key => 
            fieldMapping[key].includes('quantity') || 
            key.toLowerCase().includes('quantity') ||
            key.toLowerCase().includes('menge') ||
            key.toLowerCase().includes('anzahl')
        );
        
        if (quantityField && row[quantityField]) {
            const quantity = parseInt(row[quantityField]);
            return isNaN(quantity) ? 1 : quantity;
        }
        
        return 1;
    }

    /**
     * Extract price from CSV row
     * @param {Object} row - CSV row data
     * @param {Object} profile - Profile configuration
     * @returns {number} Price
     */
    extractPriceFromRow(row, profile) {
        const fieldMapping = profile.fieldMapping || {};
        
        // Find price mapping
        const priceField = Object.keys(fieldMapping).find(key => 
            fieldMapping[key].includes('price') || 
            key.toLowerCase().includes('price') ||
            key.toLowerCase().includes('preis')
        );
        
        if (priceField && row[priceField]) {
            return this.parsePrice(row[priceField]);
        }
        
        return 0;
    }

    /**
     * Find quantity field in row (for updating consolidated quantity)
     * @param {Object} row - CSV row data
     * @returns {string|null} Quantity field name or null
     */
    findQuantityFieldInRow(row) {
        const possibleQuantityFields = [
            'quantity', 'menge', 'anzahl', 'qty', 'amount', 'Quantity', 'Menge', 'Anzahl'
        ];
        
        for (const field of possibleQuantityFields) {
            if (row.hasOwnProperty(field)) {
                return field;
            }
        }
        
        return null;
    }

    /**
     * Extract shipping address from CSV row (matching old implementation)
     * @param {Object} row - CSV row data
     * @param {Object} profile - Profile configuration
     * @returns {Object|null} Shipping address data or null
     */
    extractShippingAddressFromRow(row, profile) {
        if (!row || !profile || !profile.fieldMapping) {
            if (DEBUG_API) {
                console.log('[CSV Processing] No row, profile, or fieldMapping provided');
            }
            return null;
        }

        const address = {};
        
        // Map shipping address fields from CSV using profile field mapping (matching old version)
        const shippingAddressFields = {
            'deliveries.shippingOrderAddress.firstName': 'firstName',
            'deliveries.shippingOrderAddress.lastName': 'lastName',
            'deliveries.shippingOrderAddress.company': 'company',
            'deliveries.shippingOrderAddress.street': 'street',
            'deliveries.shippingOrderAddress.additionalAddressLine1': 'additionalAddressLine1',
            'deliveries.shippingOrderAddress.additionalAddressLine2': 'additionalAddressLine2',
            'deliveries.shippingOrderAddress.zipcode': 'zipcode',
            'deliveries.shippingOrderAddress.city': 'city',
            'deliveries.shippingOrderAddress.country.name': 'countryName'
        };

        if (DEBUG_API) {
            console.log('[CSV Processing] Profile field mapping:', profile.fieldMapping);
            console.log('[CSV Processing] CSV row data:', row);
            console.log('[CSV Processing] Available shipping address fields:', shippingAddressFields);
        }
        
        // Find CSV columns that map to shipping address fields
        for (const [csvColumn, shopwareField] of Object.entries(profile.fieldMapping)) {
            if (DEBUG_API) {
                console.log(`[CSV Processing] Processing mapping: "${csvColumn}" -> "${shopwareField}"`);
                console.log(`[CSV Processing] CSV value for "${csvColumn}":`, row[csvColumn]);
                console.log(`[CSV Processing] Is shipping address field?:`, !!shippingAddressFields[shopwareField]);
            }
            
            if (shippingAddressFields[shopwareField] && row[csvColumn]) {
                const addressFieldName = shippingAddressFields[shopwareField];
                address[addressFieldName] = row[csvColumn].toString().trim();
                
                if (DEBUG_API) {
                    console.log(`[CSV Processing] ✅ Mapped "${csvColumn}" (${row[csvColumn]}) -> ${addressFieldName}`);
                }
            } else if (shippingAddressFields[shopwareField]) {
                if (DEBUG_API) {
                    console.log(`[CSV Processing] ❌ Skipped "${csvColumn}" -> "${shopwareField}" (empty value: "${row[csvColumn]}")`);
                }
            }
        }

        // Special handling: If additionalAddressLine1 is populated but street is empty, copy it to street
        if (address.additionalAddressLine1 && !address.street) {
            address.street = address.additionalAddressLine1;
        }

        // Handle country name to country ID conversion if needed
        if (address.countryName) {
            // Store the country name for later use in shipping method selection
            address.country = address.countryName;
            // Note: countryId conversion will be handled later by the shipping utils
        }

        // Return null if no address data found
        if (Object.keys(address).length === 0) {
            if (DEBUG_API) {
                console.log('[CSV Processing] No shipping address data found in CSV');
            }
            return null;
        }

        if (DEBUG_API) {
            console.log('[CSV Processing] Final extracted shipping address:', address);
        }

        return address;
    }

    /**
     * Normalize shipping address with required Shopware fields
     * @param {Object} csvShippingAddr - Address data from CSV
     * @param {Object} profileShippingAddr - Address data from profile (for defaults)
     * @returns {Object} Normalized shipping address
     */
    normalizeShippingAddress(csvShippingAddr, profileShippingAddr) {
        if (!csvShippingAddr) {
            return profileShippingAddr;
        }

        // Use CSV data as primary source, fall back to profile address for missing fields
        const normalizedAddress = {
            // Start with CSV data as base
            ...csvShippingAddr,
            // Fall back to profile address for name fields if missing in CSV
            firstName: csvShippingAddr.firstName || profileShippingAddr?.firstName,
            lastName: csvShippingAddr.lastName || profileShippingAddr?.lastName,
            // Keep essential IDs from profile for Shopware compatibility
            salutationId: csvShippingAddr.salutationId || profileShippingAddr?.salutationId || "018ad12f1848726ea7a9edaef1c4b9e4",
            countryId: csvShippingAddr.countryId || profileShippingAddr?.countryId || "018ad12f185c7185851e2fdd7aa8206d",
        };

        // Generate unique ID for the shipping address
        if (!normalizedAddress.id) {
            normalizedAddress.id = this.generateUUID().replace(/-/g, '');
        }

        if (DEBUG_API) {
            console.log('[CSV Processing] Normalized shipping address:', normalizedAddress);
        }

        return normalizedAddress;
    }

    /**
     * Generate UUID for addresses
     * @returns {string} UUID string
     */
    generateUUID() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : ((r & 0x3) | 0x8);
            return v.toString(16);
        });
    }
}

export default CSVProcessingUtils;